import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// En haut du composant, ajoutez :
import { useRef } from 'react';
import axios from 'axios';

import AppURL from '../components/AppUrl';


interface ReservationProps {
    language: string;
}


const Reservation = ({ language }: ReservationProps) => {
    const formRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        fullName: '',
        phone: '',
        email: '',
        guests: 2,
        date: '',
        time: '',
        preferredLanguage: 'fr', // Nouveau champ
        contactMethod: 'email'   // Nouveau champ

    });
    const [availableTimeSlots, setAvailableTimeSlots] = useState<Array<{ time: string, availableSeats: number }>>([]);
    const [, setAvailableDates] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isValid, setIsValid] = useState(false);

    useEffect(() => {
        const fetchReservations = async () => {
            if (!formData.date) return;

            try {
                const response = await axios.get(`${AppURL}/api/reservations/resver/date/${formData.date}`);
                const data = response.data;
                if (Array.isArray(data)) {
                    // Filtrer seulement les créneaux avec des places disponibles
                    const availableSlots = data.filter(slot => slot.availableSeats > 0);
                    setAvailableTimeSlots(availableSlots);
                }
            } catch (error) {
                console.error("Erreur lors du chargement des réservations existantes :", error);
            }
        };

        fetchReservations();
    }, [formData.date]);



    useEffect(() => {
        setIsValid(isFormValid());
    }, [formData]);



    useEffect(() => {
        setAvailableDates(generateAvailableDates());

        // Initialiser avec le prochain jour ouvert
        if (!formData.date) {
            const nextOpenDay = getNextOpenDay(new Date());
            setFormData(prev => ({
                ...prev,
                date: formatDate(nextOpenDay)
            }));
        }
    }, []);



    const isFormValid = () => {
        return (
            formData.fullName.trim() !== '' &&
            formData.phone.trim() !== '' &&
            formData.email.trim() !== '' &&
            formData.date !== '' &&
            isOpeningDay(formData.date) &&
            formData.time !== '' &&
            ['fr', 'en'].includes(formData.preferredLanguage) && // Validation du champ langue
            ['phone', 'email', 'both'].includes(formData.contactMethod) // Validation du champ méthode de contact
        );
    };
    // Fonction pour formater la date en YYYY-MM-DD
    const formatDate = (date: Date) => {
        return date.toISOString().split('T')[0];
    };

    // Fonction pour obtenir le prochain jour ouvert
    const getNextOpenDay = (date: Date) => {
        const result = new Date(date);
        // Compense le décalage de fuseau horaire
        result.setMinutes(result.getMinutes() - result.getTimezoneOffset());
        while (!isOpeningDay(formatDate(result))) {
            result.setDate(result.getDate() + 1);
        }
        return result;
    };



    // Vérifier si un jour est un jour d'ouverture (vendredi=5, samedi=6, dimanche=0, lundi=1)
    const isOpeningDay = (dateString: string) => {
        // Crée une date à partir de la string YYYY-MM-DD en heure locale
        const date = new Date(dateString);
        // Compense le décalage de fuseau horaire
        const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
        const day = localDate.getDay();
        return day === 0 || day === 1 || day === 5 || day === 6; // Dimanche, Lundi, Vendredi, Samedi
    };

    // Générer les dates disponibles (jours d'ouverture seulement)
    const generateAvailableDates = () => {
        const dates = [];
        const today = new Date();
        today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
        const endDate = new Date(today);
        endDate.setDate(today.getDate() + 60); // Regarder sur 60 jours

        for (let d = new Date(today); d <= endDate; d.setDate(d.getDate() + 1)) {
            if (isOpeningDay(formatDate(d))) {
                dates.push(formatDate(d));
            }
        }
        return dates;
    };




    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrorMessage('');

        const localDateStr = `${formData.date}T12:00:00`;
        const dateObj = new Date(localDateStr);

        const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const allowedDays = ['Friday', 'Saturday', 'Sunday', 'Monday'];
        const dayName = daysOfWeek[dateObj.getDay()];

        if (!allowedDays.includes(dayName)) {
            const message = language === 'fr'
                ? `Désolé, nous sommes ouverts uniquement les vendredis, samedis, dimanches et lundis.`
                : `Sorry, we are only open on Fridays, Saturdays, Sundays, and Mondays.`;

            setErrorMessage(message);
            setIsSubmitting(false);

            if (formRef.current) {
                formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            return;
        }

        const dataToSend = {
            ...formData,
            dayOfWeek: dayName,
        };

        try {
            const response = await fetch(`${AppURL}/api/reservations/create`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(dataToSend)
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Erreur lors de la réservation");
            }

            setSubmitSuccess(true);
            setTimeout(() => {
                navigate('/');
            }, 3000);

        } catch (error: any) {
            setErrorMessage(error.message || "Une erreur s'est produite");
        } finally {
            setIsSubmitting(false);
        }
    };




    return (
        <div className="min-h-screen flex flex-col items-center p-8 bg-cover bg-center bg-fixed pt-[110px]"
            style={{ backgroundImage: "url('/image/Lona2.jpg')" }}>

            <div
                ref={formRef} // Ajoutez cette ligne
                className="bg-white/80 backdrop-blur-sm rounded-xl p-8 max-w-2xl w-full shadow-lg">
                <h2 className="text-2xl font-bold text-green-950 mb-6 text-center">
                    {language === 'fr' ? 'Réservation' : 'Reservation'}
                </h2>

                {errorMessage && (
                    <div style={{
                        backgroundColor: '#ffe5e5',
                        color: '#d8000c',
                        border: '1px solid #d8000c',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}>
                        {errorMessage}
                    </div>
                )}

                {submitSuccess ? (
                    <div className="text-center py-8">
                        <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <h3 className="text-xl font-bold text-green-950 mb-2">
                            {language === 'fr' ? 'Réservation confirmée!' : 'Reservation confirmed!'}
                        </h3>
                        <p className="text-gray-600">
                            {language === 'fr'
                                ? 'Merci, nous vous attendons avec impatience!'
                                : 'Thank you, we look forward to seeing you!'}
                        </p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                                {language === 'fr' ? 'Nom complet' : 'Full Name'}
                            </label>
                            <input
                                type="text"
                                id="fullName"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-950 focus:border-green-950"
                            />
                        </div>

                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                {language === 'fr' ? 'Téléphone' : 'Phone'}
                            </label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-950 focus:border-green-950"
                            />
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-950 focus:border-green-950"
                            />
                        </div>



                        <div>
                            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                                {language === 'fr' ? 'Date' : 'Date'}
                            </label>
                            <input
                                id="date"
                                type="date"
                                name="date"
                                value={formData.date}
                                onChange={handleChange}
                                required
                                min={formatDate(new Date())}
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-950 focus:border-green-950"
                                onFocus={(e) => {
                                    if (!formData.date) {
                                        const nextOpenDay = getNextOpenDay(new Date());
                                        setFormData(prev => ({
                                            ...prev,
                                            date: formatDate(nextOpenDay)
                                        }));
                                        e.target.value = formatDate(nextOpenDay);
                                    }
                                }}
                            />

                            {/* Note avec couleur conditionnelle */}
                            <div className={`mt-2 px-3 py-2 rounded-md ${formData.date && !isOpeningDay(formData.date)
                                ? 'bg-red-50 border-l-4 border-red-500 text-red-700'
                                : 'bg-green-50 border-l-4 border-green-950 text-green-950'}`}
                            >
                                <div className="flex items-start text-sm">
                                    <svg className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>

                                    <div>
                                        {language === 'fr' ? (
                                            <p>
                                                Ouvert <span className="font-semibold">vendredi (soir seulement)</span>, lundi, samedi et dimanche
                                            </p>
                                        ) : (
                                            <p>
                                                Open <span className="font-semibold">Friday (evening only)</span>, Monday, Saturday and Sunday
                                            </p>
                                        )}
                                    </div>



                                </div>
                            </div>
                        </div>


                        <div className="mb-4">
                            <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-2">
                                {language === 'fr' ? 'Choisissez votre heure' : 'Select your time'}
                            </label>



                            <div className="relative">
                                <select
                                    id="time"
                                    name="time"
                                    value={formData.time}
                                    onChange={handleChange}
                                    required
                                    className="w-full pl-4 pr-10 py-3 rounded-lg border-2 border-gray-200 focus:border-green-950 focus:ring-2 focus:ring-green-950/20 transition-all appearance-none bg-white cursor-pointer"
                                >
                                    <option value="" disabled className="text-gray-400">
                                        {language === 'fr' ? '-- Sélectionnez --' : '-- Select --'}
                                    </option>
                                    {availableTimeSlots.map(slot => (
                                        <option
                                            key={slot.time}
                                            value={slot.time}
                                            className="py-2"
                                        >
                                            {slot.time}
                                        </option>
                                    ))}
                                </select>


                            </div>


                        </div>

                        <div>
                            <label htmlFor="guests" className="block text-sm font-medium text-gray-700 mb-1">
                                {language === 'fr' ? 'Nombre de personnes' : 'Number of guests'}
                            </label>
                            <select
                                id="guests"
                                name="guests"
                                value={formData.guests}
                                onChange={handleChange}
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-950 focus:border-green-950"
                            >
                                {(() => {
                                    // Trouver le créneau sélectionné
                                    const selectedTimeSlot = availableTimeSlots.find(slot => slot.time === formData.time);
                                    // Déterminer le nombre max (23 par défaut ou disponible selon le créneau)
                                    const max = selectedTimeSlot ? selectedTimeSlot.availableSeats : 20;
                                    // Générer les options de 1 à max
                                    const options = [];
                                    for (let i = 1; i <= max; i++) {
                                        options.push(
                                            <option key={i} value={i}>{i}</option>
                                        );
                                    }
                                    return options;
                                })()}
                            </select>

                        </div>

                        {/* Nouveau champ : Langue de communication préférée */}
                        <div>
                            <label htmlFor="preferredLanguage" className="block text-sm font-medium text-gray-700 mb-1">
                                {language === 'fr' ? 'Langue de communication préférée' : 'Preferred communication language'}
                            </label>
                            <select
                                id="preferredLanguage"
                                name="preferredLanguage"
                                value={formData.preferredLanguage}
                                onChange={handleChange}
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-950 focus:border-green-950"
                            >
                                <option value="fr">Français</option>
                                <option value="en">English</option>
                            </select>
                        </div>

                        {/* Nouveau champ : Méthode de contact préférée */}
                        <div>
                            <label htmlFor="contactMethod" className="block text-sm font-medium text-gray-700 mb-1">
                                {language === 'fr' ? 'Méthode de contact préférée' : 'Preferred contact method'}
                            </label>
                            <select
                                id="contactMethod"
                                name="contactMethod"
                                value={formData.contactMethod}
                                onChange={handleChange}
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-950 focus:border-green-950"
                            >
                                <option value="email">Email</option>
                                <option value="phone">{language === 'fr' ? 'Téléphone' : 'Phone'}</option>
                                <option value="both">{language === 'fr' ? 'Les deux' : 'Both'}</option>
                            </select>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting || !isValid}
                            className={`w-full py-3 px-4 text-white font-medium rounded-lg transition duration-200 ${isSubmitting
                                ? 'bg-gray-400 opacity-70 cursor-not-allowed'
                                : isValid
                                    ? 'bg-green-950 hover:bg-green-800'
                                    : 'bg-gray-400 cursor-not-allowed'
                                }`}
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {language === 'fr' ? 'Envoi en cours...' : 'Submitting...'}
                                </span>
                            ) : (
                                language === 'fr' ? 'Réserver' : 'Book Now'
                            )}
                        </button>
                    </form>
                )}
            </div>



        </div>
    );
};

export default Reservation;